<template>
  <div v-if="teacher" class="container mt-5">
  <!-- Teacher Info -->
  <div class="row">
    <div class="col-md-4 text-center">
      <h2>{{ teacher.user.first_name }} {{ teacher.user.last_name }}</h2>
      <p><i class="bi bi-house-door"></i> Address: {{ teacher.address }}</p>
      <p><i class="bi bi-telephone"></i> Contact: {{ teacher.contact }}</p>

      <!-- Profile Photo -->
      <div v-if="teacher.user?.profile_photo_url">
        <img :src="teacher.user.profile_photo_url" alt="Profile Photo" class="img-fluid rounded-circle" style="max-width: 150px;" />
      </div>
      <div v-else>
        <p>No profile photo available</p>
      </div>
    </div>

    <!-- Package Details -->
    <div class="col-md-8">
      <div class="card">
        <div class="card-body">
          <h4 class="card-title">Package Details</h4>
          <p><strong><i class="bi bi-calendar"></i> Package Duration:</strong> {{ teacher.account?.package?.duration || 'N/A' }} Month</p>
          <p><strong><i class="bi bi-currency-dollar"></i> Package Price:</strong> {{ teacher.account?.package?.package_price || 'N/A' }}</p>
          <p><strong><i class="bi bi-wallet"></i> Total Package Price:</strong> {{ teacher.account?.pkg_total_price || 'N/A' }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Teacher Package Items with Pagination -->
  <div class="mt-4">
    <h3>Teacher Package Items</h3>
    <div v-if="paginatedItems.length > 0">
      <ul class="list-group">
        <li v-for="(item, index) in paginatedItems" :key="index" class="list-group-item">
          <div class="row">
            <div class="col-md-6">
              <p><strong>Item ID:</strong> {{ item.id }}</p>
              <p><strong>Subject:</strong> {{ item.subject?.name || 'N/A' }}</p>
              <p><strong>Price:</strong> {{ item.item_total_price }}</p>
            </div>
            <div class="col-md-6">
              <p><strong>Student Level:</strong> {{ item.student_level?.level || 'N/A' }}</p>
              <p><strong>Student Level Price:</strong> {{ item.student_level?.st_level_price || 'N/A' }}</p>
            </div>
          </div>
        </li>
      </ul>

      <!-- Pagination Controls for Items -->
      <div class="d-flex justify-content-between mt-3">
        <div>
          <label for="itemsPerPage" class="me-2">Items per page:</label>
          <select v-model="itemsPerPage" id="itemsPerPage" class="form-select d-inline-block w-auto">
            <option :value="1">1</option>
            <option :value="5">5</option>
            <option :value="10">10</option>
          </select>
        </div>
        <div>
          <button @click="prevItemPage" class="btn btn-secondary" :disabled="itemPage === 1">Previous</button>
          <span class="mx-2">Page {{ itemPage }} of {{ itemTotalPages }}</span>
          <button @click="nextItemPage" class="btn btn-secondary" :disabled="itemPage === itemTotalPages">Next</button>
        </div>
      </div>
    </div>
    <div v-else>
      <p>No items available for this teacher.</p>
    </div>
  </div>

  <!-- Associated Institutes with Pagination -->
  <div class="mt-4">
    <h3>Associated Institutes</h3>
    <div v-if="paginatedInstitutes.length > 0">
      <ul class="list-group">
        <li v-for="(instituteInfo, index) in paginatedInstitutes" :key="index" class="list-group-item">
          <p><strong>Institute Name:</strong> {{ instituteInfo.institute?.institute_name || 'N/A' }}</p>
          <p><strong>Address:</strong> {{ instituteInfo.institute?.address || 'N/A' }}</p>
          <p><strong>Contact:</strong> {{ instituteInfo.institute?.contact || 'N/A' }}</p>
        </li>
      </ul>

      <!-- Pagination Controls for Institutes -->
      <div class="d-flex justify-content-between mt-3">
        <div>
          <label for="institutesPerPage" class="me-2">Institutes per page:</label>
          <select v-model="institutesPerPage" id="institutesPerPage" class="form-select d-inline-block w-auto">
            <option :value="1">1</option>
            <option :value="5">5</option>
            <option :value="10">10</option>
          </select>
        </div>
        <div>
          <button @click="prevInstitutePage" class="btn btn-secondary" :disabled="institutePage === 1">Previous</button>
          <span class="mx-2">Page {{ institutePage }} of {{ instituteTotalPages }}</span>
          <button @click="nextInstitutePage" class="btn btn-secondary" :disabled="institutePage === instituteTotalPages">Next</button>
        </div>
      </div>
    </div>
    <div v-else>
      <p>No institutes associated with this teacher.</p>
    </div>
  </div>
</div>

<div v-else class="text-center">
  <div class="spinner-border" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

</template>

<script>
export default {
  props: {
    teacher: {
      type: [Object, null], // Accepts both Object and null
      // type: Object,
      required: true,
      default: () => ({
        user: {},
        address: '',
        contact: '',
        items: [],
        teach_institute: [],
        account: {
          package: null,
          pkg_total_price: 0,
        },
      }),
    },
  },
  data() {
    return {
      itemPage: 1,
      itemsPerPage: 1, // Number of items per page
      institutePage: 1,
      institutesPerPage: 1, // Number of institutes per page
    };
  },
  computed: {
    paginatedItems() {
      const start = (this.itemPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.teacher.items?.slice(start, end) || [];
    },
    itemTotalPages() {
      return Math.ceil((this.teacher.items?.length || 0) / this.itemsPerPage);
    },
    paginatedInstitutes() {
      const start = (this.institutePage - 1) * this.institutesPerPage;
      const end = start + this.institutesPerPage;
      return this.teacher.teach_institute?.slice(start, end) || [];
    },
    instituteTotalPages() {
      return Math.ceil((this.teacher.teach_institute?.length || 0) / this.institutesPerPage);
    },
  },
  methods: {
    nextItemPage() {
      if (this.itemPage < this.itemTotalPages) {
        this.itemPage += 1;
      }
    },
    prevItemPage() {
      if (this.itemPage > 1) {
        this.itemPage -= 1;
      }
    },
    nextInstitutePage() {
      if (this.institutePage < this.instituteTotalPages) {
        this.institutePage += 1;
      }
    },
    prevInstitutePage() {
      if (this.institutePage > 1) {
        this.institutePage -= 1;
      }
    },
  },
};
</script>

<style scoped>
.profile-photo {
  max-width: 150px;
  max-height: 150px;
  border-radius: 50%;
  object-fit: cover;
}

.itemClass,
.pagination-controls {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

button {
  background-color: #007bff;
  color: #fff;
  border: none;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

button:hover {
  background-color: #0056b3;
}

.pagination-controls span {
  margin: 0 10px;
  font-weight: bold;
}

.spinner-border {
  margin: 20px auto;
  width: 3rem;
  height: 3rem;
  color: #007bff;
}
</style>
